import React, { useEffect, useRef, useState } from 'react';
import { Check } from '@/components/Icons/Check';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import { mailFolderIcons } from '@/pages/Mail/mailFolders';
import { MailFolderType } from '@/types/components';
import type { MailFolder } from '@/types/mail';
import { tv } from '@/utils/tv';

const chevron = tv({
  base: 'size-5 text-foreground transition-transform duration-200',
  variants: {
    isOpen: {
      true: 'rotate-180',
      false: '',
    },
  },
});

const option = tv({
  base: 'flex h-[42px] cursor-pointer items-center justify-between gap-2 rounded-xl pr-2 pl-3',
  variants: {
    isActive: {
      true: 'bg-info-surface',
      false: 'hover:bg-hover',
    },
  },
});

const optionLabel = tv({
  base: 'truncate text-left',
  variants: {
    isActive: {
      true: 'text-subhead-14 text-primary',
      false: 'text-body-14 text-muted-foreground',
    },
  },
});

interface Props {
  folders: MailFolder[];
  folder: MailFolder;
  count: number;
  onChange: (folderType: MailFolderType) => void;
}

export const MailFolderDropdown: React.FC<Props> = ({
  folders,
  folder,
  count,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const title = `${folder.name} (${count})`;

  const handleSelect = (folderType: MailFolderType) => {
    setIsOpen(false);
    onChange(folderType);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative w-fit shrink-0">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="text-title-18 flex cursor-pointer items-center gap-1 text-foreground"
      >
        {title}
        <ChevronDown className={chevron({ isOpen })} />
      </button>

      {isOpen && (
        <div
          aria-hidden
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-50 bg-black/10 animate-fade-in"
        />
      )}

      {isOpen && (
        <div className="absolute top-full left-0 z-[60] mt-2 flex w-[208px] origin-top-left flex-col gap-1 rounded-[14px] border border-transparent bg-popover p-2 shadow-[0px_3px_8px_rgba(18,31,55,0.08)] contrast:border-border animate-zoom-in">
          {folders.map((item) => {
            const isActive = item.type === folder.type;

            return (
              <button
                key={item.type}
                type="button"
                onClick={() => handleSelect(item.type)}
                className={option({ isActive })}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className={
                      isActive ? 'text-primary' : 'text-subtle-foreground'
                    }
                  >
                    {mailFolderIcons[item.type]}
                  </span>
                  <span className={optionLabel({ isActive })}>{item.name}</span>
                </span>
                {isActive && <Check className="size-4 shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
